import { Node } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import katex from "katex";
import { CALLOUTS, splitBlocks } from "@/components/MarkdownContent";

/* ------------------------------------------------------------------ *
 *  Le contenu reste stocké en Markdown. Ces extensions permettent à   *
 *  l'éditeur visuel de lire et d'écrire :                             *
 *   - les encadrés pédagogiques (titre ## « Exemple concret » + texte) *
 *   - les formules LaTeX ($x^2$ et $$...$$)                           *
 * ------------------------------------------------------------------ */

const escAttr = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "&#10;");

const renderTex = (latex, display) => {
  try {
    return katex.renderToString(latex && latex.trim() ? latex : "\\square", { throwOnError: false, displayMode: !!display });
  } catch {
    return `<code>${escAttr(latex)}</code>`;
  }
};

/** Le document accepte, au premier niveau, des blocs ordinaires et des encadrés (jamais d'encadré imbriqué). */
export const CustomDocument = Document.extend({ content: "(block|callout)+" });

export const Callout = Node.create({
  name: "callout",
  group: "callout",
  content: "(paragraph|heading|bulletList|orderedList|blockquote|table|codeBlock|mathBlock)+",
  defining: true,

  addAttributes() {
    return {
      type: { default: "exemple", rendered: false, parseHTML: (el) => el.getAttribute("data-callout") },
      title: { default: "", rendered: false, parseHTML: (el) => el.getAttribute("data-title") || "" },
      level: { default: 2, rendered: false, parseHTML: (el) => parseInt(el.getAttribute("data-level") || "2", 10) || 2 },
    };
  },
  parseHTML() { return [{ tag: "div[data-callout]" }]; },
  renderHTML({ node }) {
    const def = CALLOUTS.find((c) => c.type === node.attrs.type);
    return ["div", {
      "data-callout": node.attrs.type,
      "data-title": node.attrs.title || (def && def.label) || "",
      "data-level": node.attrs.level,
      class: `rte-callout rte-callout-${node.attrs.type}`,
    }, 0];
  },
  addCommands() {
    return {
      setCallout: (attrs) => ({ state, commands }) => {
        const { $from, $to } = state.selection;
        const range = $from.blockRange($to);
        if (!range || range.parent.type.name !== "doc") return false;
        return commands.wrapIn(this.name, attrs);
      },
      // Retire le ou les encadrés touchés par la sélection (le texte qu'ils contiennent est conservé, sans cadre)
      unsetCallout: () => ({ state, tr, dispatch }) => {
        const { from, to } = state.selection;
        const found = [];
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (node.type.name === this.name) { found.push({ node, pos }); return false; }
          return true;
        });
        if (!found.length) return false;
        if (dispatch) {
          found.reverse().forEach(({ node, pos }) => tr.replaceWith(pos, pos + node.nodeSize, node.content));
        }
        return true;
      },
    };
  },
  addStorage() {
    return {
      markdown: {
        serialize(state, node) {
          const def = CALLOUTS.find((c) => c.type === node.attrs.type);
          const level = node.attrs.level || 2;
          state.write(`${"#".repeat(level)} ${node.attrs.title || (def && def.label) || "Encadré"}`);
          state.closeBlock(node);
          node.forEach((child, _offset, i) => {
            // Un titre de même niveau (ou supérieur) fermerait l'encadré à la relecture : on le descend d'un cran.
            if (child.type.name === "heading" && child.attrs.level <= level) {
              state.write(`${"#".repeat(level + 1)} `);
              state.renderInline(child);
              state.closeBlock(child);
            } else {
              state.render(child, node, i);
            }
          });
          state.closeBlock(node);
        },
        parse: {},
      },
    };
  },
});

const mathView = (isBlock) => ({ node, editor, getPos }) => {
  const dom = document.createElement(isBlock ? "div" : "span");
  dom.className = isBlock ? "rte-math rte-math-block" : "rte-math";
  dom.contentEditable = "false";
  dom.title = "Cliquer pour modifier la formule";
  let current = node;
  const paint = () => { dom.innerHTML = renderTex(current.attrs.latex, isBlock || current.attrs.display); };
  paint();
  dom.addEventListener("click", () => {
    editor.view.dom.dispatchEvent(new CustomEvent("rte-edit-math", {
      bubbles: true,
      detail: { pos: getPos(), latex: current.attrs.latex, block: isBlock, display: !!current.attrs.display },
    }));
  });
  return {
    dom,
    update: (n) => { if (n.type !== current.type) return false; current = n; paint(); return true; },
  };
};

export const MathInline = Node.create({
  name: "mathInline",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() { return { latex: { default: "" }, display: { default: false } }; },
  parseHTML() {
    return [{ tag: "span[data-math]", getAttrs: (el) => ({ latex: el.getAttribute("data-math") || "", display: el.getAttribute("data-display") === "1" }) }];
  },
  renderHTML({ node }) { return ["span", { "data-math": node.attrs.latex, class: "rte-math" }, `$${node.attrs.latex}$`]; },
  addNodeView() { return mathView(false); },
  addStorage() {
    return { markdown: { serialize(state, node) { state.write(node.attrs.display ? `$$${node.attrs.latex}$$` : `$${node.attrs.latex}$`); }, parse: {} } };
  },
});

export const MathBlock = Node.create({
  name: "mathBlock",
  group: "block",
  atom: true,
  selectable: true,
  addAttributes() { return { latex: { default: "" } }; },
  parseHTML() { return [{ tag: "div[data-math-block]", getAttrs: (el) => ({ latex: el.getAttribute("data-math-block") || "" }) }]; },
  renderHTML({ node }) { return ["div", { "data-math-block": node.attrs.latex, class: "rte-math rte-math-block" }, `$$${node.attrs.latex}$$`]; },
  addNodeView() { return mathView(true); },
  addStorage() {
    return { markdown: { serialize(state, node) { state.write(`$$\n${node.attrs.latex}\n$$`); state.closeBlock(node); }, parse: {} } };
  },
});

/* ---------------------- Markdown -> Markdown « pour l'éditeur » ---------------------- */

const blockMath = (latex) => `\n<div data-math-block="${escAttr(latex.trim())}"></div>\n`;

const INLINE_MATH = /\$\$([^$]+?)\$\$|(?<![\\$])\$(?![\s$])((?:\\.|[^$\\])+?)(?<![\s\\])\$(?!\d)/g;

function inlineMath(line) {
  return line
    .split(/(`+[^`]*`+)/)
    .map((seg, i) => (i % 2 === 1 ? seg : seg.replace(INLINE_MATH, (_m, display, inline) => {
      const latex = display !== undefined ? display : inline;
      return `<span data-math="${escAttr(latex)}"${display !== undefined ? ' data-display="1"' : ""}></span>`;
    })))
    .join("");
}

function transformMath(text) {
  const lines = text.split("\n");
  const out = [];
  let inFence = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*(```|~~~)/.test(line)) { inFence = !inFence; out.push(line); continue; }
    if (inFence) { out.push(line); continue; }
    const m = line.match(/^\s*\$\$(.*)$/);
    if (m) {
      const rest = m[1];
      const close = rest.indexOf("$$");
      if (close !== -1) {
        if (!rest.slice(close + 2).trim()) { out.push(blockMath(rest.slice(0, close))); continue; }
      } else {
        let j = i + 1;
        const buf = [rest];
        while (j < lines.length && !lines[j].includes("$$")) { buf.push(lines[j]); j++; }
        if (j < lines.length) {
          const end = lines[j].indexOf("$$");
          buf.push(lines[j].slice(0, end));
          if (!lines[j].slice(end + 2).trim()) { out.push(blockMath(buf.join("\n"))); i = j; continue; }
        }
      }
    }
    out.push(inlineMath(line));
  }
  return out.join("\n");
}

/** Prépare le Markdown stocké pour l'éditeur : encadrés et formules deviennent des éléments reconnaissables. */
export function mdToEditorMd(md) {
  const blocks = splitBlocks(md || "");
  return blocks.map((b) => {
    const text = transformMath(b.lines.join("\n"));
    if (!b.callout) return text;
    const title = escAttr(b.title.replace(/[*_`]/g, "").trim());
    return `<div data-callout="${b.callout.type}" data-title="${title}" data-level="${b.level}">\n\n${text}\n\n</div>`;
  }).join("\n\n");
}
